/**
 * Landing FAQs: smooth open/close for the <details> items.
 * The <details> element still drives state and accessibility (and works without JS);
 * this only animates the answer panel's height. Respects prefers-reduced-motion and
 * opens the matching question when a block is selected in the theme editor.
 */
class LandingFaqsAccordion extends HTMLElement {
  connectedCallback() {
    this.duration = 320;
    this.easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.items = Array.from(this.querySelectorAll('.landing-faqs__item'));
    this.items.forEach((item) => {
      const summary = item.querySelector('.landing-faqs__summary');
      const panel = item.querySelector('.landing-faqs__panel');
      if (!summary || !panel) return;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (item.open && !item.classList.contains('is-closing')) {
          this.close(item);
        } else {
          this.open(item);
        }
      });
    });

    this.onBlockSelect = (event) => {
      const item = this.items.find((el) => el.contains(event.target) || el === event.target);
      if (item) this.open(item, true);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  open(item, instant = false) {
    const panel = item.querySelector('.landing-faqs__panel');
    const startHeight = item.open ? panel.offsetHeight : 0; // continue from mid-close
    item.classList.remove('is-closing');
    item.open = true;

    if (instant || this.reduceMotion.matches) return;

    const endHeight = panel.scrollHeight;
    this.animate(panel, startHeight, endHeight);
  }

  close(item) {
    const panel = item.querySelector('.landing-faqs__panel');

    if (this.reduceMotion.matches) {
      item.open = false;
      return;
    }

    item.classList.add('is-closing');
    const animation = this.animate(panel, panel.offsetHeight, 0);
    animation.onfinish = () => {
      // Only finish closing if the visitor didn't reopen it mid-animation
      if (item.classList.contains('is-closing')) {
        item.open = false;
        item.classList.remove('is-closing');
      }
    };
  }

  animate(panel, from, to) {
    panel.getAnimations().forEach((a) => a.cancel());
    return panel.animate(
      [
        { height: `${from}px`, opacity: from === 0 ? 0 : 1 },
        { height: `${to}px`, opacity: to === 0 ? 0 : 1 },
      ],
      { duration: this.duration, easing: this.easing }
    );
  }
}

if (!customElements.get('landing-faqs-accordion')) {
  customElements.define('landing-faqs-accordion', LandingFaqsAccordion);
}
